// Surface 04 — le vivier.
//
// Trois états, pas deux. « Archivé » mélangeait deux réalités opposées : le
// consultant parti de la société (dont on ne veut plus voir le nom) et celui
// placé chez un client (qu'on veut voir, mais ne pas proposer).

import { useState } from 'react'
import { Upload, RefreshCw, Archive, Undo2, Briefcase } from 'lucide-react'
import { get, post, type ConsultantRow } from '../lib/api'
import { fullDate, initials, teinte } from '../lib/format'
import { navigate } from '../lib/router'
import { useData, Loading, Empty, ImportCv, ActionButton, useToast } from '../components/common'
import { useConsultantStatus } from '../lib/dictionnaires'
import { useMots } from '../lib/vocabulaire'
import { useT } from '../lib/i18n'

export function Vivier({ onChanged }: { onChanged: () => void }) {
  const STATUTS = useConsultantStatus()
  const mots = useMots()
  const t = useT()
  const vivier = useData(() => get<ConsultantRow[]>('/api/consultants?all=1'), [])
  const [importOpen, setImportOpen] = useState<{ preset?: string } | null>(null)
  const [voirArchives, setVoirArchives] = useState(false)
  const toast = useToast()

  if (vivier.loading) return <Loading label={t('vivier.loading')} />
  if (vivier.error) return <Empty>{vivier.error}</Empty>

  const tous = vivier.data ?? []
  const actifs = tous.filter((c) => !c.archived)
  const archives = tous.filter((c) => c.archived)
  const enMission = actifs.filter((c) => c.status === 'placed').length
  const visibles = voirArchives ? archives : actifs

  async function changerStatut(c: ConsultantRow, status: 'available' | 'placed') {
    await post(`/api/consultants/${c.id}/status`, { status })
    toast(status === 'placed' ? t('vivier.wentOnMission', { name: c.name }) : t('vivier.backOnBench', { name: c.name }))
    vivier.reload()
    onChanged()
  }

  async function archiver(c: ConsultantRow, archived: boolean) {
    await post(`/api/consultants/${c.id}/archive`, { archived })
    toast(archived ? t('vivier.archivedToast', { name: c.name }) : t('vivier.reactivatedToast', { name: c.name }))
    vivier.reload()
    onChanged()
  }

  return (
    <>
      <div className="bar">
        <div>
          <h3>{mots('nav.profil')}</h3>
          <div className="sub">
            {t('vivier.summary', { available: actifs.length - enMission, onMission: enMission, archived: archives.length })}
          </div>
        </div>
        <div className="grow" />
        <button className={`chip${voirArchives ? ' on' : ''}`} onClick={() => setVoirArchives(!voirArchives)}>
          {voirArchives ? t('vivier.showActive') : t('vivier.showArchived', { n: archives.length })}
        </button>
        <button className="btn-primary" onClick={() => setImportOpen({})}>
          <Upload size={15} />{t('vivier.importCv')}
        </button>
      </div>

      <div className="body">
        <div className="grid3">
          {visibles.map((c) => {
            const st = STATUTS[c.status] ?? STATUTS.available
            return (
              <div
                key={c.id}
                className={`vcard${c.archived ? ' arch' : ''}`}
                tabIndex={0}
                onClick={() => navigate(`#/consultant/${c.id}`)}
              >
                <div className="vtop">
                  <span className="vav" style={teinte(c.name)}>{initials(c.name)}</span>
                  <div style={{ flex: 1 }}>
                    <div className="vn">{c.name}</div>
                    <div className="vr">{c.label}</div>
                  </div>
                  <span className={`pill ${st.cls}`}>{st.label}</span>
                </div>
                <div className="vmeta">
                  {c.stats.avg_score != null && <span>{t('vivier.avgScore')} <b>{c.stats.avg_score}</b></span>}
                  <span>{t('vivier.evaluated')} <b>×{c.stats.scored}</b></span>
                  <span>{t('vivier.positioned')} <b>×{c.stats.positioned}</b></span>
                  <span>{t('vivier.updated')} <b>{fullDate(c.updated_at)}</b></span>
                </div>
                <div className="vact" onClick={(e) => e.stopPropagation()}>
                  {c.archived ? (
                    <ActionButton onClick={() => archiver(c, false)}><Undo2 size={14} />{t('vivier.reactivate')}</ActionButton>
                  ) : (
                    <>
                      <ActionButton onClick={() => setImportOpen({ preset: c.name })}>
                        <RefreshCw size={14} />{t('common.update')}
                      </ActionButton>
                      {c.status === 'available' ? (
                        <ActionButton onClick={() => changerStatut(c, 'placed')}>
                          <Briefcase size={14} />{t('vivier.onMission')}
                        </ActionButton>
                      ) : (
                        <ActionButton onClick={() => changerStatut(c, 'available')}>
                          <Undo2 size={14} />{t('vivier.onBench')}
                        </ActionButton>
                      )}
                      <ActionButton onClick={() => archiver(c, true)}><Archive size={14} />{t('vivier.archive')}</ActionButton>
                    </>
                  )}
                </div>
              </div>
            )
          })}

          {!voirArchives && (
            <button className="dropzone" onClick={() => setImportOpen({})}>
              <Upload size={26} />
              <span className="dt">{t('vivier.dropzoneTitle')}</span>
              <span style={{ fontSize: '.76rem' }}>{t('vivier.dropzoneHint')}</span>
            </button>
          )}

          {visibles.length === 0 && voirArchives && <Empty>{t('vivier.noArchived')}</Empty>}
        </div>
      </div>

      {importOpen && (
        <ImportCv
          preset={importOpen.preset}
          onClose={() => setImportOpen(null)}
          onDone={() => { vivier.reload(); onChanged() }}
        />
      )}
    </>
  )
}
